import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import SectionHeader from '../components/ui/SectionHeader';
import DecorativeBg from '../components/ui/DecorativeBg';
import { contactsApi, homeApi } from '../api/api';

const DEFAULT_SOCIAL_LINKS = [
  { id: 'facebook', key: 'social_facebook', icon: 'fab fa-facebook-f', color: 'bg-blue-600', url: 'https://www.facebook.com/radhekrishnashastri.bapu', label: 'Facebook' },
  { id: 'instagram', key: 'social_instagram', icon: 'fab fa-instagram', color: 'bg-pink-600', url: 'https://www.instagram.com/radhekrishnabapu_official', label: 'Instagram' },
  { id: 'youtube', key: 'social_youtube', icon: 'fab fa-youtube', color: 'bg-red-600', url: 'https://www.youtube.com/@Radhekrishnabapuofficial', label: 'YouTube' },
];

/** English labels — used to parse API "Label: value" bodies */
const PHONE_LINES = [
  { key: 'ashramOffice', label: 'Ashram Office' },
  { key: 'eventsInfo', label: 'Events Info' },
  { key: 'sevaCoordination', label: 'Seva Coordination' },
  { key: 'emergency', label: 'Emergency' },
];

const EMAIL_LINES = [
  { key: 'general', label: 'General' },
  { key: 'events', label: 'Events' },
  { key: 'seva', label: 'Seva' },
  { key: 'guidance', label: 'Guidance' },
];

const EMPTY_ASHRAM = {
  phones: {
    ashramOffice: '',
    eventsInfo: '',
    sevaCoordination: '',
    emergency: '',
  },
  emails: {
    general: '',
    events: '',
    seva: '',
    guidance: '',
  },
};

function parseLabeledBody(body, lines) {
  const out = {};
  if (!body) return out;
  String(body).replace(/\r\n/g, '\n').split('\n').forEach((line) => {
    const m = line.match(/^([^:]+):\s*(.*)$/);
    if (!m) return;
    const label = m[1].trim().toLowerCase();
    const value = m[2].trim();
    const match = lines.find((l) => l.label.toLowerCase() === label);
    if (match) out[match.key] = value;
  });
  return out;
}

function mergeLabeledCard(raw, fallback, lines) {
  if (!raw || typeof raw !== 'object') return { ...fallback };
  const fromBody = raw.body != null && String(raw.body).trim()
    ? parseLabeledBody(raw.body, lines)
    : {};
  const next = {};
  lines.forEach(({ key }) => {
    const direct = raw[key] != null ? String(raw[key]).trim() : '';
    next[key] = direct || fromBody[key] || fallback[key] || '';
  });
  return next;
}

function BodyLines({ text, className }) {
  const lines = String(text || '').split('\n').filter((l) => l.trim() !== '');
  return (
    <p className={className}>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {line}
          {i < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      ))}
    </p>
  );
}

function LabeledLines({ data, lines, className }) {
  const rows = lines
    .map(({ key, label }) => {
      const value = (data[key] || '').trim();
      return value ? `${label}: ${value}` : null;
    })
    .filter(Boolean);
  return (
    <p className={className}>
      {rows.map((line, i) => (
        <React.Fragment key={i}>
          {line}
          {i < rows.length - 1 ? <br /> : null}
        </React.Fragment>
      ))}
    </p>
  );
}

const Contact = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', subject: '', message: '', _honey: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [socialLinks, setSocialLinks] = useState(DEFAULT_SOCIAL_LINKS);
  const [ashram, setAshram] = useState(EMPTY_ASHRAM);

  const phoneDisplayLines = useMemo(
    () => PHONE_LINES.map(({ key }) => ({ key, label: t(`contactPage.phones.${key}`) })),
    [t]
  );
  const emailDisplayLines = useMemo(
    () => EMAIL_LINES.map(({ key }) => ({ key, label: t(`contactPage.emails.${key}`) })),
    [t]
  );

  const sanskritLines = t('contactPage.hero.sanskrit').split('\n');

  useEffect(() => {
    homeApi
      .getAll()
      .then((res) => {
        if (!res.success || !res.data) return;
        const d = res.data;
        setSocialLinks(
          DEFAULT_SOCIAL_LINKS.map((item) => {
            const raw = d[item.key];
            const url = raw && typeof raw === 'object' && (raw.url || '').trim()
              ? String(raw.url).trim()
              : item.url;
            return { ...item, url };
          })
        );
        setAshram({
          phones: mergeLabeledCard(d.ashram_phones, EMPTY_ASHRAM.phones, PHONE_LINES),
          emails: mergeLabeledCard(d.ashram_emails, EMPTY_ASHRAM.emails, EMAIL_LINES),
        });
      })
      .catch(() => {
        setAshram(EMPTY_ASHRAM);
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await contactsApi.submit(form);
      if (res.success) {
        setSubmitted(true);
        setForm({ firstName: '', lastName: '', phone: '', subject: '', message: '', _honey: '' });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        setSubmitError(res.message || t('contactPage.form.error_fail'));
      }
    } catch {
      setSubmitError(t('contactPage.form.error_network'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setSubmitError('');
  };

  return (
    <>
      <Helmet>
        <title>{t('contactPage.meta.title')}</title>
        <meta name="description" content={t('contactPage.meta.description')} />
        <link rel="canonical" href="https://www.shreeramranujadham.org/contact" />
        <meta property="og:title" content={t('contactPage.meta.title')} />
        <meta property="og:description" content={t('contactPage.meta.description')} />
        <meta property="og:url" content="https://www.shreeramranujadham.org/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.shreeramranujadham.org/logo.jpg" />
      </Helmet>

      {/* ── Page Header ─────────────────────────────────── */}
      <section
        className="pt-24 pb-6 md:pt-28 md:pb-16 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 50%, #7c3aed 100%)' }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>

        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 bg-opacity-20 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-200 bg-opacity-20 rounded-full translate-y-40 -translate-x-40"></div>

        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-400 bg-opacity-10 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-yellow-400 bg-opacity-10 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="mb-2 md:mb-8">
            <div className="inline-flex items-center bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-6 py-3 mb-6">
              <i className="fas fa-hands-praying text-yellow-300 mr-3 text-xl"></i>
              <span className="text-white font-semibold">{t('contactPage.hero.badge')}</span>
            </div>
          </div>
          <h1 className="text-2xl md:text-7xl lg:text-8xl font-bold text-white mb-3 md:mb-8 leading-tight drop-shadow-lg">
            {t('contactPage.hero.title_1')}<br />
            <span className="text-yellow-300">{t('contactPage.hero.title_2')}</span>
          </h1>
          <p className="text-xs md:text-xl text-white max-w-3xl mx-auto leading-relaxed mb-3 md:mb-8 drop-shadow-md font-medium">
            {t('contactPage.hero.subtitle')}
          </p>

          <div className="max-w-2xl mx-auto bg-black bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 border border-white border-opacity-30 shadow-2xl">
            <blockquote className="text-xl text-white font-semibold italic leading-relaxed mb-4">
              {sanskritLines.map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < sanskritLines.length - 1 ? <br /> : null}
                </React.Fragment>
              ))}
            </blockquote>
            <div className="w-16 h-px bg-yellow-300 mx-auto mb-4"></div>
            <p className="text-base text-white font-medium leading-relaxed">
              &ldquo;{t('contactPage.hero.quote')}&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* ── Contact Information Section ───────────────── */}
      <section className="pt-8 pb-4 md:pt-24 md:pb-12 bg-gradient-to-br from-gray-50 to-orange-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-200 bg-opacity-20 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-yellow-200 bg-opacity-20 rounded-full translate-y-40 -translate-x-40"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center bg-white bg-opacity-80 backdrop-blur-sm rounded-full px-6 py-3 mb-6 shadow-md border border-orange-100">
                <i className="fas fa-phone-alt text-orange-600 mr-3 text-lg"></i>
                <span className="text-orange-700 font-semibold">{t('contactPage.info.badge')}</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-bold text-gray-800 mb-8 leading-tight">
                {t('contactPage.info.title_1')}<br />
                <span className="text-orange-500 font-bold">{t('contactPage.info.title_2')}</span>
              </h2>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-16">
              <div
                className="group rounded-3xl p-8 shadow-xl border-2 hover:-translate-y-1 relative overflow-hidden transition-all duration-500"
                style={{ background: 'linear-gradient(to bottom right, #ffffff, #fff7ed)', borderColor: '#ffedd5' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#fdba74';
                  const title = e.currentTarget.querySelector('h3');
                  if (title) title.style.color = '#c2410c';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#ffedd5';
                  const title = e.currentTarget.querySelector('h3');
                  if (title) title.style.color = '';
                }}
              >
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: 'linear-gradient(to bottom right, rgba(255, 237, 213, 0.5), rgba(254, 249, 195, 0.5))' }}
                ></div>
                <div className="relative z-10 text-center">
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl border-2 transition-transform duration-300"
                    style={{ background: 'linear-gradient(to bottom right, #fed7aa, #fef08a)', borderColor: '#fdba74' }}
                  >
                    <i className="fas fa-map-marker-alt text-3xl" style={{ color: '#ea580c' }}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4 transition-colors">{t('contactPage.info.address_title')}</h3>
                  <BodyLines className="text-gray-750 leading-relaxed text-sm mb-4" text={t('footer.default_address')} />
                  <div className="mt-6">
                    <span className="font-medium text-xs px-3 py-1 rounded-full" style={{ color: '#ea580c', background: '#ffedd5' }}>{t('contactPage.info.address_badge')}</span>
                  </div>
                </div>
              </div>

              <div className="group bg-gradient-to-br from-white to-green-50 rounded-3xl p-8 shadow-xl border-2 border-green-100 hover:border-green-300 hover:-translate-y-1 relative overflow-hidden transition-all duration-550">
                <div className="absolute inset-0 bg-gradient-to-br from-green-100/50 to-emerald-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative z-10 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-200 to-emerald-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl border-2 border-green-300 transition-transform duration-300">
                    <i className="fas fa-phone text-3xl text-green-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4 group-hover:text-green-700 transition-colors">{t('contactPage.info.phone_title')}</h3>
                  <LabeledLines className="text-gray-755 leading-relaxed text-sm mb-4" data={ashram.phones} lines={phoneDisplayLines} />
                  <div className="mt-6">
                    <span className="text-green-600 font-medium text-xs bg-green-100 px-3 py-1 rounded-full">{t('contactPage.info.phone_badge')}</span>
                  </div>
                </div>
              </div>

              <div className="group bg-gradient-to-br from-white to-purple-50 rounded-3xl p-8 shadow-xl border-2 border-purple-100 hover:border-purple-300 hover:-translate-y-1 relative overflow-hidden transition-all duration-550">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-100/50 to-indigo-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative z-10 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-200 to-indigo-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl border-2 border-purple-300 transition-transform duration-300">
                    <i className="fas fa-envelope text-3xl text-purple-600"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4 group-hover:text-purple-700 transition-colors">{t('contactPage.info.email_title')}</h3>
                  <LabeledLines className="text-gray-755 leading-relaxed text-sm mb-4" data={ashram.emails} lines={emailDisplayLines} />
                  <div className="mt-6">
                    <span className="text-purple-600 font-medium text-xs bg-purple-100 px-3 py-1 rounded-full">{t('contactPage.info.email_badge')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Contact Form & Map ─────────────────────────── */}
      <section className="pt-8 pb-8 md:pt-24 md:pb-12 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 bg-orange-300 rounded-full"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 bg-yellow-300 rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-red-300 rounded-full"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16">
              <div>
                <div className="mb-8">
                  <div className="inline-flex items-center bg-green-100 rounded-full px-6 py-3 mb-6">
                    <i className="fas fa-envelope-open text-green-500 mr-3"></i>
                    <span className="text-green-600 font-semibold">{t('contactPage.form.badge')}</span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                    {t('contactPage.form.title_1')}<br />
                    <span className="text-green-500 font-bold">{t('contactPage.form.title_2')}</span>
                  </h2>
                  <p className="text-xl text-gray-600 leading-relaxed font-medium">
                    {t('contactPage.form.subtitle')}
                  </p>
                </div>

                {submitted && (
                  <div className="bg-green-150 border-l-4 border-green-600 text-green-800 p-4 rounded-xl mb-6 shadow font-medium">
                    {t('contactPage.form.success')}
                  </div>
                )}

                <form onSubmit={handleSubmit} autoComplete="off" className="relative space-y-6 bg-gradient-to-br from-orange-50 to-yellow-50 p-8 rounded-3xl shadow-xl border border-orange-100">
                  {/* Honeypot — leave empty; bots that fill it are discarded server-side */}
                  <div className="absolute -left-[9999px] opacity-0 h-0 w-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="company_website">Website</label>
                    <input
                      type="text"
                      id="company_website"
                      name="_honey"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form._honey}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">{t('contactPage.form.first_name')}</label>
                      <input
                        type="search"
                        inputMode="text"
                        id="firstName"
                        name="firstName"
                        required
                        value={form.firstName}
                        onChange={handleInputChange}
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                        className="w-full px-4 py-3 border border-orange-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 bg-white"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">{t('contactPage.form.last_name')}</label>
                      <input
                        type="search"
                        inputMode="text"
                        id="lastName"
                        name="lastName"
                        value={form.lastName}
                        onChange={handleInputChange}
                        autoComplete="off"
                        autoCapitalize="off"
                        autoCorrect="off"
                        spellCheck={false}
                        className="w-full px-4 py-3 border border-orange-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 bg-white"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">{t('contactPage.form.phone')}</label>
                    <input
                      type="search"
                      inputMode="tel"
                      id="phone"
                      name="phone"
                      required
                      value={form.phone}
                      onChange={handleInputChange}
                      autoComplete="off"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      className="w-full px-4 py-3 border border-orange-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 bg-white"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">{t('contactPage.form.subject')}</label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      value={form.subject}
                      onChange={handleInputChange}
                      autoComplete="off"
                      className="w-full px-4 py-3 border border-orange-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 bg-white text-gray-700"
                    >
                      <option value="">{t('contactPage.form.select_topic')}</option>
                      <option value="general">{t('contactPage.form.subjects.general')}</option>
                      <option value="satsang">{t('contactPage.form.subjects.satsang')}</option>
                      <option value="katha">{t('contactPage.form.subjects.katha')}</option>
                      <option value="seva">{t('contactPage.form.subjects.seva')}</option>
                      <option value="spiritual">{t('contactPage.form.subjects.spiritual')}</option>
                      <option value="ashram">{t('contactPage.form.subjects.ashram')}</option>
                      <option value="donation">{t('contactPage.form.subjects.donation')}</option>
                      <option value="other">{t('contactPage.form.subjects.other')}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">{t('contactPage.form.message')}</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="6"
                      value={form.message}
                      onChange={handleInputChange}
                      autoComplete="off"
                      autoCapitalize="off"
                      autoCorrect="off"
                      spellCheck={false}
                      className="w-full px-4 py-3 border border-orange-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-200 bg-white text-gray-700"
                      placeholder={t('contactPage.form.placeholder')}
                    ></textarea>
                  </div>

                  {submitError && (
                    <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-2">
                      <i className="fas fa-exclamation-circle mr-2"></i>{submitError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 px-6 rounded-lg font-bold text-white transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(to right, #f97316, #dc2626)', border: 'none' }}
                  >
                    {submitting
                      ? <><i className="fas fa-spinner fa-spin"></i>{t('contactPage.form.sending')}</>
                      : <><i className="fas fa-paper-plane"></i>{t('contactPage.form.submit')}</>
                    }
                  </button>
                </form>
              </div>

              <div>
                <div className="mb-8">
                  <div className="inline-flex items-center bg-green-100 rounded-full px-6 py-3 mb-6">
                    <i className="fas fa-map-marked-alt text-green-500 mr-3"></i>
                    <span className="text-green-600 font-semibold">{t('contactPage.map.badge')}</span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
                    {t('contactPage.map.title_1')}<br />
                    <span className="text-green-500 font-bold">{t('contactPage.map.title_2')}</span>
                  </h2>
                  <p className="text-xl text-gray-600 leading-relaxed font-medium">
                    {t('contactPage.map.subtitle')}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8 shadow-xl border border-green-100 mb-8 animate-fade-in-up">
                  <div className="bg-white rounded-2xl overflow-hidden shadow-lg h-80 mb-6 border-2 border-green-200">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3691.845!2d71.4554605!3d21.7322707!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39589b17833f5173%3A0x33324c124b6534af!2sShree%20Ram%20Ranuja%20Dham%20Ashram!5e0!3m2!1sen!2sin!4v1726584000000!5m2!1sen!2sin"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      title={t('contactPage.map.iframe_title')}
                    ></iframe>
                  </div>
                  <a
                    href="https://www.google.com/maps/place/Shree+Ram+Ranuja+Dham+Ashram/data=!4m2!3m1!1s0x0:0x33324c124b6534af"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full py-3 px-6 rounded-xl font-semibold text-white mb-4 transition-all duration-200 hover:opacity-90"
                    style={{ background: 'linear-gradient(to right, #16a34a, #15803d)' }}
                  >
                    <i className="fas fa-map-marker-alt mr-2"></i>
                    View Shree Ram Ranuja Dham Ashram on Google Maps &amp; Get Directions
                  </a>

                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-green-200">
                    <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                      <i className="fas fa-route text-green-600 mr-3 text-2xl"></i>
                      {t('contactPage.map.directions_title')}
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0"
                            style={{ background: '#ffedd5' }}
                          >
                            <i className="fas fa-car text-sm" style={{ color: '#ea580c' }}></i>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800 text-sm">{t('contactPage.map.by_car')}</p>
                            <p className="text-xs text-gray-500 leading-relaxed">{t('contactPage.map.by_car_desc')}</p>
                          </div>
                        </div>

                        <div className="flex items-start">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                            <i className="fas fa-train text-blue-600 text-sm"></i>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800 text-sm">{t('contactPage.map.by_train')}</p>
                            <p className="text-xs text-gray-500 leading-relaxed">{t('contactPage.map.by_train_desc')}</p>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex items-start">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                            <i className="fas fa-bus text-green-600 text-sm"></i>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800 text-sm">{t('contactPage.map.by_bus')}</p>
                            <p className="text-xs text-gray-500 leading-relaxed">{t('contactPage.map.by_bus_desc')}</p>
                          </div>
                        </div>

                      
                      </div>
                    </div>

                    <div className="mt-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-orange-400 p-4 rounded-lg">
                      <p className="text-xs text-gray-705 leading-relaxed font-medium">
                        🪷 <strong>{t('contactPage.map.note_label')}</strong> {t('contactPage.map.note')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Ashram Facilities ─────── */}
      <section className="pt-8 pb-12 md:pt-16 md:pb-24 bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50 relative overflow-hidden">
        <DecorativeBg variant="warm" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              badge={{ icon: 'fas fa-place-of-worship', text: t('contactPage.facilities.badge') }}
              title={<>{t('contactPage.facilities.title_1')} <span className="text-orange-500 font-bold">{t('contactPage.facilities.title_2')}</span></>}
              subtitle={t('contactPage.facilities.subtitle')}
              mb="mb-16"
            />

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 hover:shadow-xl transition-all duration-300" style={{ borderColor: '#ffedd5' }}>
                <div
                  className="w-16 h-16 shrink-0 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md"
                  style={{ background: 'linear-gradient(to right, #f97316, #ef4444)', aspectRatio: '1 / 1' }}
                >
                  <i className="fas fa-om text-white text-2xl leading-none"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-3 text-center">{t('contactPage.facilities.meditation_title')}</h4>
                <p className="text-gray-600 text-center text-sm leading-relaxed">
                  {t('contactPage.facilities.meditation_desc')}
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 hover:shadow-xl transition-all duration-300" style={{ borderColor: '#dcfce7' }}>
                <div
                  className="w-16 h-16 shrink-0 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md"
                  style={{ background: 'linear-gradient(to right, #22c55e, #10b981)', aspectRatio: '1 / 1' }}
                >
                  <i className="fas fa-users text-white text-2xl leading-none"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-3 text-center">{t('contactPage.facilities.events_title')}</h4>
                <p className="text-gray-600 text-center text-sm leading-relaxed">
                  {t('contactPage.facilities.events_desc')}
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 hover:shadow-xl transition-all duration-300" style={{ borderColor: '#f3e8ff' }}>
                <div
                  className="w-16 h-16 shrink-0 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md"
                  style={{ background: 'linear-gradient(to right, #a855f7, #6366f1)', aspectRatio: '1 / 1' }}
                >
                  <i className="fas fa-home text-white text-2xl leading-none"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-800 mb-3 text-center">{t('contactPage.facilities.guest_title')}</h4>
                <p className="text-gray-600 text-center text-sm leading-relaxed">
                  {t('contactPage.facilities.guest_desc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ Section ─────────────────────────────────── */}
      <section className="py-12 md:py-24 bg-white relative overflow-hidden border-t border-gray-100">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <SectionHeader
              badge={{ icon: 'fas fa-question-circle', text: t('contactPage.faq.badge') }}
              title={<>{t('contactPage.faq.title_1')} <span className="text-orange-500 font-bold">{t('contactPage.faq.title_2')}</span></>}
              subtitle={t('contactPage.faq.subtitle')}
              mb="mb-12"
              titleSize="text-3xl md:text-5xl"
            />
            <div className="space-y-6">
              <div className="bg-gray-50 border border-gray-100 p-6 rounded-2xl shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('contactPage.faq.q1')}</h3>
                <p className="text-gray-700">{t('contactPage.faq.a1')}</p>
              </div>

              <div className="bg-gray-50 border border-gray-100 p-6 rounded-2xl shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('contactPage.faq.q2')}</h3>
                <p className="text-gray-700">{t('contactPage.faq.a2')}</p>
              </div>

              <div className="bg-gray-50 border border-gray-100 p-6 rounded-2xl shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('contactPage.faq.q3')}</h3>
                <p className="text-gray-700">{t('contactPage.faq.a3')}</p>
              </div>

              <div className="bg-gray-50 border border-gray-100 p-6 rounded-2xl shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('contactPage.faq.q4')}</h3>
                <p className="text-gray-700">{t('contactPage.faq.a4')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Social Links ──────────────────────────────────── */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-orange-50 border-t border-gray-100">
        <div className="container mx-auto px-4 social-links-section">
          <h2 className="text-2xl font-bold text-gray-800 mb-8">{t('contactPage.social_title')}</h2>
          <div className="social-links-row">
            {socialLinks.map((s) => (
              <a
                key={s.id}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`social-link-btn ${s.color}`}
                aria-label={s.label}
              >
                <i className={s.icon}></i>
              </a>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
